<script setup lang="ts">
  import { useMenuStore } from '@/composition/use-menu';
  import { useOrdering } from '@/composition/use-ordering';
  import { useRestaurant } from '@/composition/use-restaurant';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { ref, computed, inject } from 'vue';
  import { useRoute, useRouter } from 'vue-router/composables';
  import ModalPosNewOrderSummaryFloating from './ModalPosNewOrderSummaryFloating.vue';

  const cartOpen = ref(false);

  const restaurantHelper = useRestaurant();
  const restaurantStore = useRestaurantStore();
  const menuStore = useMenuStore();
  const orderHelper = inject<ReturnType<typeof useOrdering>>('orderHelper');
  const cart = computed(() => orderHelper.cart.value);

  const actionButton = ref<HTMLButtonElement | null>(null);

  const route = useRoute();
  const router = useRouter();
</script>

<template>
  <div class="cart-button-container">
    <div
      class="cart-floating-button"
      v-bind="$attrs"
      :class="{ closed: cartOpen }"
      v-show="menuStore.canOrder"
    >
      <div class="inner">
        <v-btn
          ref="actionButton"
          class="action-button"
          color="primary"
          rounded
          large
          @click="cartOpen = !cartOpen"
        >
          <div class="details">
            <span class="quantity">
              {{ orderHelper.currentCartTotalItems.value }} productos
            </span>
            <span class="total">
              ${{ displayPrice(orderHelper.currentCartTotal.value) }}
            </span>
          </div>
          <v-spacer></v-spacer>
          <div class="action">
            <v-icon size="28">mdi-food-takeout-box</v-icon>
            <span class="action-text">Ver pedido</span>
          </div>
        </v-btn>
      </div>
    </div>
    <ModalPosNewOrderSummaryFloating v-model="cartOpen" />
  </div>
</template>

<style scoped lang="scss">
  .cart-button-container {
    .cart-floating-button {
      @apply tw-absolute tw-left-1/2 tw-transform tw--translate-x-1/2 tw-transition-transform tw-pointer-events-none -tw-translate-y-[3.25rem];
      @apply tw-z-50;
      @apply tw-w-[20rem] md:tw-w-[24rem] tw-max-w-full;

      .inner {
        @apply md:tw-rounded-t-xl tw-px-4;
        @apply tw-flex tw-flex-col tw-items-center tw-relative;

        .delivery-min {
          @apply tw-bg-orange-500 tw-text-white tw-font-light tw-text-sm tw-text-center tw-pt-0 tw-px-2 tw-rounded-t-xl;
        }

        .action-button {
          @apply tw-px-4 tw-pl-5 tw-pb-1 tw-pt-2 tw-w-full tw-justify-between tw-rounded-full tw-pointer-events-auto;

          .action {
            @apply tw-flex tw-items-center tw-space-x-1;
          }
        }

        .details {
          @apply tw-flex tw-flex-col tw-items-start tw-justify-center;

          .quantity {
            @apply tw-text-xs;
            line-height: 1em;

            @screen sm {
              line-height: 1rem;
            }
          }

          .total {
            @apply tw-font-bold tw-text-xl;
          }
        }
      }

      &.closed {
        @apply tw-translate-y-[140%];
      }
    }
  }
</style>
